import Navbar from "../components/Navbar";
import { useState, useEffect } from "react";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import LoginRequired from "../components/LoginRequired";
import { useViewPermission } from "../context/PermissionContext";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  dropdownListBody,
  normalizeListResponse,
} from "../utils/listPagination";
import * as topicsApi from "../api/topics";

export default function Explore() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [topics, setTopics] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const { hasPermission, loading: permLoading } = useViewPermission("view_self_learning");

  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || permLoading || !hasPermission) {
      setLoading(false);
      return;
    }

    const fetchTopics = async () => {
      try {
        const res = await topicsApi.list(dropdownListBody());
        const topicsList = normalizeListResponse(res.data).data;

        const grouped = {};

        topicsList.forEach((t) => {
          const key = `${t.subjectId}-${t.gradeId}`;

          if (!grouped[key]) {
            grouped[key] = {
              subjectId: t.subjectId,
              subjectName: t.subjectName,
              gradeId: t.gradeId,
              gradeName: t.gradeName,
              topics: [],
            };
          }

          grouped[key].topics.push({
            topicId: t.id,
            topicName: t.topicName,
            weightage: t.weightage || 1,
          });
        });

        setTopics(Object.values(grouped));
      } catch (err) {
        console.error("Error fetching topics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, [isAuthenticated, hasPermission, permLoading]);

  if (authLoading || (isAuthenticated && permLoading)) {
    return (
      <div className="text-center">
        <Navbar />
        <div style={{ paddingTop: 120 }}>
          <Loader />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div>
        <Navbar />
        <LoginRequired message="Please sign in to explore self-learning topics and materials." />
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <>
          {/* Title */}
          <h1
            className="text-center mt-5"
            style={{
              fontSize: "2.2rem",
              fontWeight: 600,
              color: "#2c3e50",
            }}
          >
            Explore
          </h1>

          <div className="container mt-4">
            {/* Loading */}
            {loading && (
              <div className="text-center mt-5">
                <h4 style={{ color: "#7f8c8d" }}>Thinking...</h4>
              </div>
            )}

            {/* Empty State */}
            {!loading && topics.length === 0 && (
              <div className="text-center mt-5">
                <p style={{ color: "#7f8c8d" }}>
                  No content available at the moment.
                </p>
              </div>
            )}

            {/* Cards */}
            <div className="row gx-4 gy-4">
              {topics.map((topic, idx) => (
                <div
                  key={`${topic.subjectId}-${topic.gradeId}`}
                  className="col-12 col-sm-6 col-md-4 col-lg-3"
                >
                  <div
                    onClick={() =>
                      navigate(`/explore/${topic.subjectId}/${topic.gradeId}`)
                    }
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      height: "120px",
                      borderRadius: "18px",
                      background:
                        hoveredCard === idx && !hoveredDropdown
                          ? "#edf6ff"
                          : "#f8fafc",
                      boxShadow:
                        hoveredCard === idx
                          ? "0 12px 30px rgba(0, 0, 0, 0.12)"
                          : "0 4px 12px rgba(0, 0, 0, 0.06)",
                      transform:
                        hoveredCard === idx ? "translateY(-6px)" : "none",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      position: "relative",
                      padding: "16px",
                      textAlign: "center",
                      overflow: "visible",
                      zIndex: hoveredCard === idx ? 10 : 1,
                      border: "1px solid rgba(0,0,0,0.03)",
                    }}
                  >
                    {/* Bottom Accent */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: "16px",
                        right: "16px",
                        height: "3px",
                        backgroundColor:
                          hoveredCard === idx ? "#3498db" : "#d0d7de",
                        borderRadius: "2px",
                      }}
                    />

                    {/* Grade Badge */}
                    <div
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: "700",
                        color: "#3498db",
                        background: "rgba(52,152,219,0.1)",
                        padding: "3px 10px",
                        borderRadius: "999px",
                        marginBottom: "6px",
                      }}
                    >
                      {topic.gradeName}
                    </div>

                    {/* Subject */}
                    <h5
                      style={{
                        fontSize: "1.15rem",
                        fontWeight: "700",
                        color: "#1f2d3d",
                        marginBottom: "2px",
                      }}
                    >
                      {topic.subjectName}
                    </h5>

                    {/* Topic Count */}
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "#7f8c8d",
                        margin: 0,
                      }}
                    >
                      {topic.topics.length} topics
                    </p>

                    {/* 🔥 Hover Dropdown (Overlay, NOT resizing card) */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "100%",
                        marginTop: "2px", // optional tiny spacing without breaking hover
                        left: "50%",
                        transform:
                          hoveredCard === idx
                            ? "translate(-50%, 0)"
                            : "translate(-50%, 10px)",
                        width: "90%",
                        background: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
                        padding: "10px",
                        opacity: hoveredCard === idx ? 1 : 0,
                        pointerEvents: hoveredCard === idx ? "auto" : "none",
                        transition: "all 0.25s ease",
                        zIndex: 100,
                      }}
                    >
                      <div
                        style={{
                          maxHeight: "260px", // ✅ shows ~10+ items
                          overflowY: "auto",
                          paddingRight: "4px",
                        }}
                      >
                        {topic.topics.map((t) => (
                          <div
                            key={t.topicId}
                            onClick={() =>
                              navigate(
                                `/explore/${topic.subjectId}/${topic.gradeId}?topicId=${t.topicId}`,
                              )
                            }
                            onMouseEnter={() => setHoveredDropdown(t.id)}
                            onMouseLeave={() => setHoveredDropdown(null)}
                            style={{
                              padding: "8px 12px",
                              margin: "5px 0",
                              borderRadius: "8px",
                              background:
                                hoveredDropdown === t.id
                                  ? "#edf6ff"
                                  : "#f8fafc",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              transform:
                                hoveredDropdown === t.id
                                  ? "translateX(2px)"
                                  : "none",
                              boxShadow:
                                hoveredDropdown === t.id
                                  ? "0 2px 6px rgba(52,152,219,0.15)"
                                  : "none",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                color:
                                  hoveredDropdown === t.id
                                    ? "#2c3e50"
                                    : "#34495e",
                              }}
                            >
                              {t.topicName}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
    </div>
  );
}
